import React, { useContext, useMemo, useEffect } from 'react';

import { useToken } from './useToken';
import { PermissionsProvider } from '../Permissions';
import { useSetPermissions } from './useSetPermissions';

export type UserIdentity = {
  connection: string;
  isSocial: boolean;
  provider: string;
  user_id: string;
};

export type UserType = {
  name?: string;
  picture?: string;
  nickname?: string;

  app_metadata?: { superAdmin?: boolean };
  identities?: UserIdentity[];
};

export type UseAuthResult = {
  token: string;
  user?: UserType | null;

  isSuper: boolean;
  isLoading: boolean;
  isAuthorized: boolean;
  isAuthenticated: boolean;

  permissions: PermissionType[];

  logout: () => void;
  loginWithRedirect: () => void;
};

const UserContext = React.createContext<UseAuthResult>({
  token: '',

  isSuper: false,
  isLoading: false,
  isAuthorized: false,
  isAuthenticated: false,

  permissions: [],
  logout: () => {
    console.log('logout: no user context');
  },
  loginWithRedirect: () => {
    console.log('loginWithRedirect: no user context');
  },
});

type UserProviderProps = {
  children: React.ReactNode;
};

const AutoLoginRedirect = () => {
  const { loginWithRedirect } = useAuth();

  useEffect(() => {
    loginWithRedirect();
  }, [loginWithRedirect]);

  return null;
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const tokenValue = useToken();
  const userValue = useSetPermissions(
    tokenValue.token,
    tokenValue.isLoading,
    tokenValue.loginWithRedirect
  );

  const value = useMemo(() => {
    const isSuper = !!tokenValue?.user?.app_metadata?.superAdmin;
    return {
      ...tokenValue,
      ...userValue,
      isSuper: isSuper,
      isLoading: tokenValue.isLoading || !userValue.inited,
      isAuthorized: isSuper || !!userValue.permissions.length,
    };
  }, [tokenValue, userValue]);

  if (value.isLoading) return null;

  return (
    <UserContext.Provider value={value}>
      {value.isAuthenticated ? (
        <PermissionsProvider
          permissions={value.permissions.map(p => ({
            action: p,
            allowed: true,
          }))}
        >
          {children}
        </PermissionsProvider>
      ) : (
        <AutoLoginRedirect />
      )}
    </UserContext.Provider>
  );
};

export const useAuth = () => useContext(UserContext);
export { useUserSettings } from './useUserSettings';
